import { storage } from '../../../js/utils/storage.js';
import { getNsfwEnabled } from '../../../js/utils/userPrefs.js';
import { applyOutfitNames, loadOutfitMappings } from '../../utils/outfitMappings.js';
import { parseNumericId } from '../shared/id-utils.js';
import { orderRespondingCharacters } from '../shared/speaking-order.js';
import {
    getUserVibe,
    getUserReasoningEffort,
    getUserTemperature,
    getUserTextGenerationParams,
    getUserModelOverride
} from './constants.js';

export const enrichCharactersWithOutfits = async (characters) => {
    try {
        await loadOutfitMappings();
        applyOutfitNames(characters);
    } catch (e) {
        console.warn('Failed to load outfit names:', e);
        applyOutfitNames(characters);
    }
};

export const SPRITE_PREVENT_REPETITION_KEY = 'spritePreventRepetition';
export const SPRITE_USE_SMART_SELECTION_KEY = 'spriteUseSmartSelection';
export const SPRITE_HIDE_SMART_UNAVAILABLE_KEY = 'spriteHideSmartUnavailable';

const readStoredBoolean = (key, fallback = true) => {
    const value = storage.get(key);
    if (value === undefined || value === null || value === '') return fallback;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (normalized === 'true' || normalized === '1') return true;
        if (normalized === 'false' || normalized === '0') return false;
    }
    return fallback;
};

export const getSpriteSelectionOptions = () => ({
    preventRepetition: readStoredBoolean(SPRITE_PREVENT_REPETITION_KEY, true),
    useSmartSelection: readStoredBoolean(SPRITE_USE_SMART_SELECTION_KEY, true)
});

export const setSpriteSelectionOption = (key, value) => {
    storage.set(key, Boolean(value));
};

export const getHideSmartUnavailable = () => readStoredBoolean(SPRITE_HIDE_SMART_UNAVAILABLE_KEY, false);

export const setHideSmartUnavailable = (value) => {
    storage.set(SPRITE_HIDE_SMART_UNAVAILABLE_KEY, Boolean(value));
};

export const buildSpriteSelections = (characters) => {
    const spriteGallerySelections = storage.get('spriteGallerySelections') || {};
    const spriteSelections = [];

    characters.forEach((char) => {
        const outfitId = char.outfitId || 0;
        const charIdKey = String(char.id);
        const outfitKey = String(outfitId);
        const keys = spriteGallerySelections?.[charIdKey]?.[outfitKey];

        if (!Array.isArray(keys) || keys.length === 0) return;

        const selections = keys
            .map((key) => {
                if (typeof key !== 'string') return null;
                const parts = key.split('_');
                if (parts.length < 3) return null;
                const actionId = parseInt(parts[2], 10);
                return Number.isNaN(actionId) ? null : actionId;
            })
            .filter(Boolean);

        if (selections.length > 0) {
            spriteSelections.push({ characterId: char.id, outfitId, selections });
        }
    });

    return spriteSelections;
};

const isGreetingCharacterCandidate = (character) => {
    if (!character || typeof character !== 'object') return false;
    if (character.id === undefined || character.id === null || String(character.id).trim() === '') return false;

    const hasUsefulCharacterData = (
        character.name !== undefined ||
        character.outfitId !== undefined ||
        character.gender !== undefined ||
        character.goal !== undefined ||
        character.actionId !== undefined ||
        character.bodytype !== undefined ||
        character.isActive !== undefined
    );

    return hasUsefulCharacterData;
};

const getGreetingCharacterScore = (character) => {
    const scoreKeys = ['name', 'outfitId', 'gender', 'goal', 'actionId', 'bodytype', 'isActive'];
    return scoreKeys.reduce((score, key) => score + (character?.[key] !== undefined ? 1 : 0), 0);
};

const normalizeGreetingCharacters = (characters, hangoutConfig) => {
    const canonicalCharacters = [];
    const dedupedById = new Map();

    const incoming = Array.isArray(characters) ? characters : [];
    incoming.forEach((character) => {
        if (!isGreetingCharacterCandidate(character)) return;

        const normalizedId = String(character.id);
        const sanitizedCharacter = {
            ...character,
            id: (typeof character.id === 'string' && /^\d+$/.test(character.id)) ? parseInt(character.id, 10) : character.id
        };

        const existing = dedupedById.get(normalizedId);
        if (!existing || getGreetingCharacterScore(sanitizedCharacter) > getGreetingCharacterScore(existing)) {
            dedupedById.set(normalizedId, sanitizedCharacter);
        }
    });

    canonicalCharacters.push(...dedupedById.values());

    if (canonicalCharacters.length > 0) return canonicalCharacters;

    const configCharacters = Array.isArray(hangoutConfig?.allCharacters) ? hangoutConfig.allCharacters : [];
    return configCharacters.filter(isGreetingCharacterCandidate);
};

export const isResolvedConversationId = (conversationId) => {
    if (typeof conversationId !== 'string') return false;
    const normalizedConversationId = conversationId.trim();
    if (!normalizedConversationId) return false;
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidPattern.test(normalizedConversationId);
};

export const buildGreetingRequest = ({ conversationId, scenarioId, characters, currentTime, hangoutConfig, variation = 0 }) => {
    const parsedVariation = Number.parseInt(String(variation ?? 0), 10);
    const modelOverride = getUserModelOverride();
    return {
        ...(isResolvedConversationId(conversationId) ? { conversationId: conversationId.trim() } : {}),
        scenarioId,
        characters: normalizeGreetingCharacters(characters, hangoutConfig),
        currentTime,
        language: storage.getLanguage(),
        variation: Number.isFinite(parsedVariation) && parsedVariation >= 0 ? parsedVariation : 0,
        nsfwAllowed: getNsfwEnabled(),
        isPrivateScenario: hangoutConfig?.isPrivateScenario === true,
        ...(modelOverride ? { modelOverride } : {})
    };
};

export const buildTextGenRequestFields = async () => {
    const userVibe = await getUserVibe();
    const reasoning = await getUserReasoningEffort();
    const temperature = await getUserTemperature();
    const textGenerationParams = await getUserTextGenerationParams();
    const modelOverride = getUserModelOverride();
    return {
        promptId: userVibe,
        ...(modelOverride ? { modelOverride } : {}),
        reasoning,
        temperature,
        maxTokens: textGenerationParams.maxTokens,
        contextTurns: textGenerationParams.contextTurns,
        ...(textGenerationParams.topP != null && { topP: textGenerationParams.topP }),
        ...(textGenerationParams.frequencyPenalty != null && { frequencyPenalty: textGenerationParams.frequencyPenalty }),
        ...(textGenerationParams.presencePenalty != null && { presencePenalty: textGenerationParams.presencePenalty }),
        ...(textGenerationParams.customPrompt && { customPrompt: textGenerationParams.customPrompt }),
        nsfwAllowed: getNsfwEnabled()
    };
};

export const anyCharacterMentioned = (message, characters) =>
    Array.isArray(characters) && characters.some((c) => message.includes(`@${c.name}`));

export const characterShouldRespond = (message, character, anyMentions) =>
    !anyMentions || message.includes(`@${character.name}`);

export const filterRespondingCharacters = (message, characters = []) => {
    const anyMentions = anyCharacterMentioned(message, characters);
    return characters.filter((c) => characterShouldRespond(message, c, anyMentions));
};

export { orderRespondingCharacters };

export const speakingOrderIds = (characters = []) =>
    (Array.isArray(characters) ? characters : [])
        .map((character) => parseNumericId(character?.id ?? character?.characterId) ?? character?.id ?? character?.characterId)
        .filter((id) => id !== undefined && id !== null && String(id).trim() !== '');

export const mapHangoutRequestCharacters = ({
    characters = [],
    conversationHistory = [],
    message = '',
    respondIds = null,
    includeShouldRespond = false
} = {}) => {
    const anyMentions = respondIds == null ? anyCharacterMentioned(message, characters) : null;
    return characters.map((char) => {
        const lastMessageForChar = conversationHistory
            .slice()
            .reverse()
            .find((msg) => msg.role === 'assistant' && String(msg.characterId) === String(char.id));
        const hasOutfitChanged = lastMessageForChar && String(lastMessageForChar.outfitId) !== String(char.outfitId);
        const shouldRespond = respondIds != null
            ? respondIds.has(String(char.id))
            : characterShouldRespond(message, char, anyMentions);
        const resultChar = {
            id: parseNumericId(char.id) ?? char.id,
            outfitId: parseNumericId(char.outfitId) ?? 0,
            outfitName: char.outfitName || 'default outfit',
            name: char.name,
            ...((includeShouldRespond || shouldRespond === false) && { shouldRespond }),
            ...(hasOutfitChanged && { outfitChanged: true }),
            ...(char.actionId !== undefined && { currentAction: parseNumericId(char.actionId) ?? char.actionId })
        };
        if (char.voice_id) resultChar.voice_id = char.voice_id;
        if (char.voice_language) resultChar.voice_language = char.voice_language;
        return resultChar;
    });
};
